import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { NodeViewWrapper } from "@tiptap/react";
import {
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
	Preview,
} from "../../../../../../../../../../common/components";
import styles from "./LineReference.module.css";
import { resetDocument } from "../../../../../../../../slice/notes-selected-document/notes-selected-document-slice";
import { NavUtils } from "../../../../../../../../../../navigation";
import {
	resetPdf,
	setHoveredInformation,
	setScrollTo,
	setSelection,
} from "../../../../../../../../slice/notes-pdf/notes-pdf-slice";
import { useApi, useTrackEvent } from "../../../../../../../../../../common/hooks";
import { ContentService } from "../../../../../../../../../../api";
import { translate } from "../../../../../../../../../../common/providers";

const LineReference = ({ deleteNode, node }) => {
	const { call: getPreviewInformationChapter } = useApi(ContentService.getPreviewInformationChapter);
	const { content, documentId, documentName, refType, itemId } = node.attrs;
	const documentIdRedux = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.selectedDocument?.id);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const [isClicked, setIsClicked] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [preview, setPreview] = useState(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const trackEvent = useTrackEvent();
	const handleSelectDocument = () => {
		if (documentId !== documentIdRedux) {
			dispatch(resetDocument());
			dispatch(resetPdf());
		}
		navigate(NavUtils.goToNotes({ projectId, documentId }));
		setIsClicked(true);
	};
	const scrollToElement = () => {
		setIsClicked(false);
		if (documentIdRedux !== documentId) {
			handleSelectDocument();
		}
		dispatch(setHoveredInformation({ id: itemId, highlighted: false }));
		if (refType === "INFORMATION") {
			getPreviewInformationChapter({ docId: documentId, informationId: itemId })
				.then((data) => {
					setIsClicked(true);
					dispatch(
						setScrollTo({
							page: data.pageStart,
							y1: data.rectangles?.[0]?.y1,
							y2: data.rectangles?.[0]?.y2,
						})
					);
					dispatch(setHoveredInformation({ id: itemId, highlighted: true }));
					dispatch(setSelection({}));
				})
				.catch(console.error);
		}
		trackEvent("Notes_Reference_Highlight");
	};
	const handleIconRender = () => {
		if (refType === "INFORMATION") {
			return icon.faSitemap;
		}
		if (refType === "DOCUMENT") {
			return icon.faFileAlt;
		}
		return icon.faFileAlt;
	};
	const handleClosePreview = () => {
		setPreview(null);
	};
	const handleDeleteNode = (e) => {
		e.stopPropagation();
		deleteNode();
		trackEvent("Notes_Delete_Reference");
	};
	const handleOpenPreviewDocument = () => {
		setPreview({ documentId, pageStart: 1 });
	};
	const handleOpenPreviewInformation = () => {
		getPreviewInformationChapter({
			docId: documentId,
			informationId: itemId,
		})
			.then((data) => {
				setPreview({
					informationId: itemId,
					rectangles: data.rectangles,
					pageStart: data.pageStart,
					documentId,
				});
			})
			.catch(console.error);
	};
	const handleOpenPreview = (e) => {
		e.stopPropagation();
		if (refType === "DOCUMENT") {
			handleOpenPreviewDocument();
		}
		if (refType === "INFORMATION") {
			handleOpenPreviewInformation();
		}
		trackEvent("Notes_Reference_Preview");
	};
	const tooltipTitle = () => (
		<>
			<CustomTooltip title={translate("common:component.preview.title")}>
				<CustomIconButton icon={icon.faFile} size="xs" onClick={handleOpenPreview} />
			</CustomTooltip>
			<span className={styles.seperator} />
			<CustomTooltip title={translate("common:btn.delete")}>
				<CustomIconButton icon={icon.faTrash} size="xs" onClick={handleDeleteNode} />
			</CustomTooltip>
		</>
	);
	return (
		<NodeViewWrapper className={styles.wrapperContainer} data-drag-handle="">
			<div
				className={styles.containerSmall}
				data-drag-handle=""
				data-is-clicked={isClicked}
				role="presentation"
				onClick={scrollToElement}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
			>
				{hovered && <div className={styles.tooltipDisplay}>{tooltipTitle()}</div>}
				<IconComponent className={styles.iconComponent} color="var(--blue-500)" icon={handleIconRender()} />
				<CustomTooltip showWhenOverflow title={content}>
					<div className={styles.text}>{refType === "INFORMATION" ? content : documentName}</div>
				</CustomTooltip>
				<Preview
					coordinates={preview?.rectangles}
					docId={preview?.documentId}
					infoId={preview?.informationId}
					infoType={preview?.informationType}
					open={!!preview}
					page={preview?.pageStart}
					projectId={projectId}
					onClose={handleClosePreview}
				/>
			</div>
		</NodeViewWrapper>
	);
};

export default LineReference;
